import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type NotificationView } from '../lib/api'

const TYPE_LABEL: Record<NotificationView['type'], string> = {
  ticket_assigned: 'Ticket assigned',
  chat_assigned: 'Chat assigned',
  task_assigned: 'Task assigned',
  ticket_escalated: 'Reply target approaching',
  task_reminder: 'Task reminder',
  automation: 'Automation rule',
  number_health: 'WhatsApp number',
}

const fmt = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

/**
 * Everything that was raised for this person specifically — a ticket or task handed to them,
 * or a reply target closing in. Read-state lives per notification rather than per type, since
 * dismissing one ticket assignment should never hide a different one still waiting.
 */
export function Notifications() {
  const queryClient = useQueryClient()
  const notificationsQuery = useQuery({ queryKey: ['notifications'], queryFn: () => api.notifications() })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['notifications'] })
  const markRead = useMutation({ mutationFn: (id: string) => api.markNotificationRead(id), onSuccess: refresh })
  const markAllRead = useMutation({ mutationFn: () => api.markAllNotificationsRead(), onSuccess: refresh })

  if (notificationsQuery.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (notificationsQuery.isError) {
    const message = notificationsQuery.error instanceof ApiError ? notificationsQuery.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const items = notificationsQuery.data!.items
  const unread = items.filter((n) => !n.readAt)

  return (
    <>
      <header className="stack gap-1">
        <span className="cad-eyebrow">Notifications</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">{unread.length} unread</h1>
          {unread.length > 0 && (
            <button
              type="button"
              className="cad-btn"
              data-variant="text"
              disabled={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              Mark all read
            </button>
          )}
        </div>
        {(markRead.error || markAllRead.error) && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {(() => {
              const err = markRead.error ?? markAllRead.error
              return err instanceof ApiError || err instanceof Error ? err.message : 'That did not go through.'
            })()}
          </p>
        )}
      </header>

      {items.length === 0 ? (
        <div className="cad-card">
          <p className="cad-muted">Nothing here yet. Ticket assignments, task assignments, and reply-target
            warnings will show up as they happen.</p>
        </div>
      ) : (
        <div className="stack gap-2">
          {items.map((n) => (
            <div
              key={n.id}
              className="cad-card row between gap-3 wrap"
              style={{ opacity: n.readAt ? 0.65 : 1, alignItems: 'flex-start' }}
            >
              <div className="stack gap-1">
                <div className="row gap-2" style={{ alignItems: 'center' }}>
                  <span className="cad-pill" data-tone={n.readAt ? undefined : 'accent'}>{TYPE_LABEL[n.type]}</span>
                  <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{fmt(n.createdAt)}</span>
                </div>
                <span style={{ fontWeight: 500 }}>{n.title}</span>
                {n.body && <span className="cad-muted">{n.body}</span>}
              </div>
              {!n.readAt && (
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="text"
                  disabled={markRead.isPending}
                  onClick={() => markRead.mutate(n.id)}
                >
                  Mark read
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  )
}
